// Outlet colour coding — field-app side.
//
// Admin sets colour rules (e.g. "No order in 90 days" = red) on the admin
// "Outlet colour coding" screen; here we read the colour guide (legend) and
// the colour of each outlet. Backend: /api/outlet-colors/legend + /lookup.
//
// Everything here fails silently on purpose: if the feature is off for this
// user, or a call fails, screens simply look exactly as they did before (no
// colours, no guide, no error toasts). skipAuthRedirect keeps a hiccup on
// these optional calls from ever logging the user out.
import { useEffect, useState } from "react";
import { api, getToken } from "@/lib/api";

const LEGEND_TTL_MS = 5 * 60 * 1000;
const LEGEND_RETRY_MS = 60 * 1000;   // after a failed call, try again in 1 min
const LOOKUP_CHUNK = 2000;            // backend limit per /lookup call
const OFF = Object.freeze({ enabled: false, rules: [] });
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

// Filter key for "outlets with no colour" in the colour guide.
export const NO_COLOUR = "__none__";

export const isHexColor = (c) => typeof c === "string" && HEX_RE.test(c);

// Keep only well-formed legend rows: [{id, name, color}].
export function cleanLegend(rules) {
  if (!Array.isArray(rules)) return [];
  return rules
    .filter((r) => r && r.id && isHexColor(r.color))
    .map((r) => ({ id: String(r.id), name: String(r.name || ""), color: r.color }));
}

// An outlet's colour tag ({rule_id, name, color}) or null when unusable.
export function cleanTag(tag) {
  return tag && isHexColor(tag.color) ? tag : null;
}

// Legend is the same for every screen, so it's kept in memory for 5 min
// (1 min after a failed call). Keyed by login token so switching user on a
// shared phone refetches.
let legendCache = null; // { token, at, ttl, promise }

export function fetchLegend() {
  try {
    const token = getToken() || "";
    const now = Date.now();
    if (legendCache && legendCache.token === token && now - legendCache.at < legendCache.ttl) {
      return legendCache.promise;
    }
    const entry = { token, at: now, ttl: LEGEND_TTL_MS, promise: null };
    entry.promise = api.get("/outlet-colors/legend", { skipAuthRedirect: true })
      .then(({ data }) => {
        const rules = cleanLegend(data?.rules);
        return data?.enabled && rules.length ? { enabled: true, rules } : OFF;
      })
      .catch(() => {
        entry.ttl = LEGEND_RETRY_MS;
        return OFF;
      });
    legendCache = entry;
    return entry.promise;
  } catch {
    return Promise.resolve(OFF);
  }
}

// Forget the cached colour guide (colours were switched off, or a call failed)
// so the next screen asks again.
export function clearLegendCache() {
  legendCache = null;
}

// {retailer_id: {rule_id, name, color}} for the given outlets. Outlets with
// no colour are simply absent. Returns null when colours are off for this
// user or on any error (the caller then shows no colours and no guide).
export async function fetchOutletColors(ids) {
  const uniq = [...new Set((ids || []).filter(Boolean))];
  if (!uniq.length) return {};
  const out = {};
  try {
    for (let i = 0; i < uniq.length; i += LOOKUP_CHUNK) {
      const { data } = await api.post(
        "/outlet-colors/lookup",
        { retailer_ids: uniq.slice(i, i + LOOKUP_CHUNK) },
        { skipAuthRedirect: true },
      );
      if (!data?.enabled) return null;
      for (const [rid, tag] of Object.entries(data.colors || {})) {
        const t = cleanTag(tag);
        if (t) out[rid] = t;
      }
    }
  } catch {
    return null;
  }
  return out;
}

// Colours + legend for a list of outlet ids. Refetches only when the SET of
// ids changes (compared as a sorted joined string, so a re-sorted or freshly
// built array with the same outlets doesn't trigger another call).
export function useOutletColors(ids) {
  const key = [...new Set((ids || []).filter(Boolean))].sort().join(",");
  const [state, setState] = useState({ colors: {}, legend: [], enabled: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // No outlets (yet) — nothing to colour, so no guide either.
      const lg = key ? await fetchLegend() : OFF;
      if (cancelled) return;
      if (!lg.enabled) {
        setState({ colors: {}, legend: [], enabled: false });
        return;
      }
      const colors = await fetchOutletColors(key.split(","));
      if (cancelled) return;
      if (colors === null) {
        clearLegendCache();
        setState({ colors: {}, legend: [], enabled: false });
        return;
      }
      // A tag for a rule the cached guide doesn't know yet (rule added in the
      // last few minutes) — refresh the guide so its pill and count show.
      let legend = lg.rules;
      const known = new Set(legend.map((r) => r.id));
      if (Object.values(colors).some((t) => !known.has(t.rule_id))) {
        clearLegendCache();
        const fresh = await fetchLegend();
        if (cancelled) return;
        legend = fresh.enabled ? fresh.rules : legend;
      }
      if (!cancelled) setState({ colors, legend, enabled: true });
    })();
    return () => { cancelled = true; };
  }, [key]);

  return state;
}

// {rule_id: n, [NO_COLOUR]: n} for the colour guide's counts.
export function countByColour(items, getTag) {
  const out = {};
  for (const it of items || []) {
    const k = getTag(it)?.rule_id || NO_COLOUR;
    out[k] = (out[k] || 0) + 1;
  }
  return out;
}

// Does an outlet's tag pass the colour-guide filter? (null filter = all)
export function matchesColour(tag, active) {
  if (!active) return true;
  if (active === NO_COLOUR) return !tag;
  return tag?.rule_id === active;
}

// A filter left pointing at a colour that's no longer in the guide (rule
// turned off, feature switched off) is treated as "All".
export function activeColourIn(legend, active) {
  if (!active) return null;
  if (active === NO_COLOUR) return legend.length ? active : null;
  return legend.some((r) => r.id === active) ? active : null;
}
